/**
 * Admin → Agents list (Phase 28.10).
 *
 * Shows all 10 pipeline agents. Enabled toggle is structural — submitting
 * it opens a GitHub PR rather than writing directly to DDB. A success toast
 * shows the PR URL.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { listAdminAgents, updateAdminAgent } from "../services/adminAgents.js";
import { useStore } from "../store/useStore.js";
import ErrorBubble from "../components/ErrorBubble.jsx";
import LoadingSkeleton from "../components/LoadingSkeleton.jsx";

export default function AdminAgentsList() {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const addToast = useStore((s) => s.addToast);

  const load = useCallback(() => {
    setLoading(true);
    return listAdminAgents()
      .then((data) => {
        setAgents(data.agents || []);
        setError(null);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function handleToggle(agentId, next) {
    const previous = agents;
    setTogglingId(agentId);
    setAgents((list) =>
      list.map((a) => (a.agentId === agentId ? { ...a, enabled: next } : a)),
    );
    try {
      const existing = agents.find((a) => a.agentId === agentId);
      const result = await updateAdminAgent(agentId, { ...existing, enabled: next });
      if (result.prUrl) {
        addToast({
          type: "info",
          message: `Pending PR: ${result.prUrl}`,
        });
        // Revert optimistic update — actual change lands after PR merges
        setAgents(previous);
      }
      setError(null);
    } catch (err) {
      setError(err.message);
      setAgents(previous);
    } finally {
      setTogglingId(null);
    }
  }

  return (
    <div className="admin-page">
      <div className="admin-page__header">
        <h1>Agents</h1>
        <Link to="/admin/agents/new" className="btn btn-primary">
          New agent
        </Link>
      </div>

      <p className="admin-page__subtext">
        Structural changes (dependencies, output key, tools, enable/disable)
        open a pull request for review. Prompt edits write directly.{" "}
        <Link to="/admin/agents/pending-prs">View pending PRs →</Link>
      </p>

      {error && <ErrorBubble message={error} />}
      {loading ? (
        <LoadingSkeleton variant="line" />
      ) : agents.length === 0 ? (
        <p className="admin-page__empty">No agents registered. Seed the database first.</p>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Agent ID</th>
              <th>Dependencies</th>
              <th>Enabled</th>
              <th>Updated</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {agents.map((a) => (
              <tr key={a.agentId}>
                <td>
                  <Link to={`/admin/agents/${encodeURIComponent(a.agentId)}/edit`}>
                    {a.name}
                  </Link>
                </td>
                <td>
                  <code>{a.agentId}</code>
                </td>
                <td>
                  {(a.dependsOn || []).length > 0 ? (
                    <span className="admin-badge">
                      {(a.dependsOn || []).length} dep
                      {(a.dependsOn || []).length !== 1 ? "s" : ""}
                    </span>
                  ) : (
                    <span className="admin-muted">root</span>
                  )}
                </td>
                <td>
                  <label className="admin-toggle">
                    <input
                      type="checkbox"
                      checked={Boolean(a.enabled)}
                      disabled={togglingId === a.agentId}
                      onChange={(e) => handleToggle(a.agentId, e.target.checked)}
                      aria-label={`${a.enabled ? "Disable" : "Enable"} ${a.name}`}
                    />
                    <span>{a.enabled ? "Enabled" : "Disabled"}</span>
                  </label>
                </td>
                <td>{a.updatedAt ? new Date(a.updatedAt).toLocaleDateString() : "—"}</td>
                <td>
                  <Link
                    to={`/admin/agents/${encodeURIComponent(a.agentId)}/edit`}
                    className="admin-action-link"
                  >
                    Edit
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
